/**
 * ScanInboxPanel — Jul-2026 (User-Wunsch, Beleg-Eingang aus Lieferschein-Scan).
 * Nicht-Lager-Belege (Werkzeug/Bürobedarf/Übernahmeschein), fremde und eigene
 * Rechnungen zur Prüfung gegen Kontoabgänge — mit Erledigt-Aktion.
 */
import { useEffect, useState, useCallback } from "react";
import { Inbox, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export const ScanInboxPanel = () => {
    const [items, setItems] = useState(null);

    const load = useCallback(() => {
        api.get("/buchhaltung/inbox?status=open")
            .then((r) => setItems(r.data.items || []))
            .catch(() => setItems([]));
    }, []);
    useEffect(() => { load(); }, [load]);

    const done = async (id) => {
        try {
            await api.post(`/buchhaltung/inbox/${id}/done`);
            toast.success("Beleg als geprüft/erledigt markiert");
            load();
        } catch { toast.error("Aktion fehlgeschlagen"); }
    };

    if (items === null || items.length === 0) return null;
    return (
        <div className="bg-white border border-indigo-300 rounded-sm p-4 mb-4" data-testid="buchhaltung-scan-inbox">
            <h3 className="font-bold text-sm uppercase tracking-wider inline-flex items-center gap-2 text-indigo-800 mb-2">
                <Inbox className="w-4 h-4" /> Beleg-Eingang aus Scan ({items.length}) — zur Prüfung gegen Kontoabgänge
            </h3>
            <ul className="space-y-1.5">
                {items.map((it) => (
                    <li key={it.id} className="flex flex-wrap items-center gap-2 border border-gray-200 rounded-sm px-2.5 py-1.5 text-xs" data-testid={`scan-inbox-row-${it.id}`}>
                        <span className="text-gray-400 font-mono-num whitespace-nowrap">{it.created_at ? new Date(it.created_at).toLocaleDateString("de-DE") : ""}</span>
                        <span className="font-bold">{it.supplier_name || "Unbekannter Lieferant"}</span>
                        {(it.delivery_note_number || it.invoice_number) && (
                            <span className="text-gray-500">Nr. {it.invoice_number || it.delivery_note_number}</span>
                        )}
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm border ${it.non_stock ? "bg-orange-50 text-orange-800 border-orange-200" : "bg-indigo-50 text-indigo-800 border-indigo-200"}`}>
                            {it.non_stock ? "Nicht-Lager" : (it.doc_type === "invoice" ? "Rechnung" : "Beleg")}
                        </span>
                        <span className="text-gray-500 truncate max-w-[280px]" title={it.reason}>{it.reason}</span>
                        {it.scanned_by_name && <span className="text-gray-400">gescannt: {it.scanned_by_name}</span>}
                        <span className="ml-auto inline-flex items-center gap-1.5">
                            {it.file_url && (
                                <a href={it.file_url} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-700 underline"
                                    data-testid={`scan-inbox-open-${it.id}`}>
                                    <ExternalLink className="w-3 h-3" /> {it.file_name || "Datei"}
                                </a>
                            )}
                            <button type="button" onClick={() => done(it.id)}
                                className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 border border-emerald-300 rounded-sm px-2 py-1 hover:bg-emerald-50"
                                data-testid={`scan-inbox-done-${it.id}`}>
                                <Check className="w-3 h-3" /> Geprüft
                            </button>
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
};
